/**
 * Atlas — Marketplace page: search verified pros, request quotes.
 */
import React, { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePros, useProperties } from '../hooks/useData';
import { marketplaceAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Panel, Avatar, Chip, Spinner, EmptyState } from '../components/common/UIComponents';

const ProMap = lazy(() => import('../components/marketplace/ProMap'));

const ALL_TRADES = [
  '', 'General Contractor', 'Plumber', 'Electrician', 'HVAC',
  'Roofer', 'Painter', 'Carpenter', 'Landscaper', 'Tile & Flooring',
  'Windows & Doors', 'Inspector', 'Foundation', 'Mason',
  'Pest Control', 'Appliance Repair', 'Cleaning Service',
];

const URGENCY_OPTIONS = [
  { value: 'now',   label: 'ASAP',          sub: 'Emergency or urgent' },
  { value: 'week',  label: 'This week',      sub: 'Within 7 days' },
  { value: 'month', label: 'This month',     sub: 'Within 30 days' },
  { value: 'plan',  label: 'Planning ahead', sub: 'No rush, get a quote' },
];

export default function MarketplacePage() {
  const [trade, setTrade] = useState('');
  const [query, setQuery] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'map'
  const [selectedPro, setSelectedPro] = useState(null);
  const [quoteTarget, setQuoteTarget] = useState(null);

  const { pros, loading } = usePros({
    trade:         trade       || undefined,
    q:             query       || undefined,
    verified_only: verifiedOnly || undefined,
  });

  return (
    <div className="page-wrap">
      <div className="page-header">
        <div>
          <div className="mono" style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
            Service Marketplace
          </div>
          <h1 className="serif" style={{ fontSize: 32, margin: '4px 0 0', color: 'var(--forest)', fontWeight: 600 }}>
            Verified Professionals
          </h1>
        </div>

        {/* View toggle */}
        <div style={{ display: 'flex', border: '1px solid var(--hairline)', borderRadius: 6, overflow: 'hidden' }}>
          {[
            { mode: 'grid', icon: '▦', label: 'Grid' },
            { mode: 'map',  icon: '⊙', label: 'Map'  },
          ].map(({ mode, icon, label }) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: '7px 16px', border: 'none', cursor: 'pointer',
                background: viewMode === mode ? 'var(--forest)' : 'var(--card)',
                color: viewMode === mode ? 'var(--paper)' : 'var(--ink-3)',
                fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <span>{icon}</span> {label}
            </button>
          ))}
        </div>
      </div>

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          placeholder="Search pros…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            width: 'min(240px, 100%)', height: 34, padding: '0 12px',
            border: '1px solid var(--hairline)', borderRadius: 4,
            background: 'var(--card)', fontSize: 13, outline: 'none',
          }}
        />
        <Chip active={verifiedOnly} onClick={() => setVerifiedOnly(!verifiedOnly)}
          style={{ cursor: 'pointer', height: 28, padding: '0 10px', fontSize: 12 }}>
          ✓ PropAtlas Verified
        </Chip>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
        {ALL_TRADES.map((t) => (
          <Chip key={t || 'all'} active={trade === t} onClick={() => setTrade(t)}
            style={{ cursor: 'pointer', height: 26, padding: '0 9px', fontSize: 11 }}>
            {t || 'All trades'}
          </Chip>
        ))}
      </div>

      {/* Pro grid / map */}
      {loading ? (
        <Spinner />
      ) : pros.length === 0 ? (
        <EmptyState message="No professionals found matching your criteria." />
      ) : viewMode === 'map' ? (
        <Suspense fallback={<Spinner />}>
          <ProMap
            pros={pros}
            onSelectPro={(pro) => setSelectedPro(pro)}
            onRequestQuote={(pro) => setQuoteTarget(pro)}
          />
        </Suspense>
      ) : (
        <div className="grid-cards">
          {pros.map((pro) => (
            <ProCard
              key={pro.id}
              pro={pro}
              onClick={() => setSelectedPro(pro)}
              onQuote={(e) => { e.stopPropagation(); setQuoteTarget(pro); }}
            />
          ))}
        </div>
      )}

      {selectedPro && (
        <ProDetailModal
          pro={selectedPro}
          onClose={() => setSelectedPro(null)}
          onRequestQuote={() => { setQuoteTarget(selectedPro); setSelectedPro(null); }}
        />
      )}

      {quoteTarget && (
        <RequestQuoteModal
          pro={quoteTarget}
          onClose={() => setQuoteTarget(null)}
        />
      )}
    </div>
  );
}

function ProCard({ pro, onClick, onQuote }) {
  const initials = pro.business_name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Panel className="lift" style={{ padding: 18, cursor: 'pointer' }} onClick={onClick}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <Avatar initials={initials} size={48} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div className="serif" style={{ fontSize: 16, fontWeight: 600 }}>{pro.business_name}</div>
            {pro.is_atlas_verified && (
              <span style={{ background: 'var(--forest)', color: 'var(--paper)', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 10, letterSpacing: '0.06em' }}>
                VERIFIED
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{pro.trade}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 12 }}>
        <span>★ <strong>{pro.rating}</strong> <span style={{ color: 'var(--ink-4)' }}>({pro.review_count})</span></span>
        {pro.response_time && <span style={{ color: 'var(--ink-4)' }}>· {pro.response_time}</span>}
        {pro.rate_band && <span style={{ color: 'var(--ink-4)', marginLeft: 'auto' }}>{pro.rate_band}</span>}
      </div>

      {(pro.suburb || pro.state) && (
        <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 8 }}>
          📍 {[pro.suburb, pro.state].filter(Boolean).join(', ')}
        </div>
      )}

      {pro.bio && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 12 }}>
          {pro.bio.length > 90 ? pro.bio.slice(0, 90) + '…' : pro.bio}
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
        {pro.badges.slice(0, 3).map((b) => (
          <Chip key={b} variant="forest" style={{ fontSize: 10, height: 20, padding: '0 7px' }}>{b}</Chip>
        ))}
      </div>

      <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', flex: 1 }}>
          {pro.atlas_jobs_completed} jobs via PropAtlas
        </span>
        <button
          className="btn ghost sm"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          style={{ fontSize: 11 }}
        >
          Profile
        </button>
        <button className="btn primary sm" onClick={onQuote}>Request quote</button>
      </div>
    </Panel>
  );
}

function ProDetailModal({ pro, onClose, onRequestQuote }) {
  const navigate = useNavigate();
  const initials = pro.business_name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ background: 'var(--forest)', color: 'var(--paper)', padding: 28 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'rgba(245,241,232,0.15)', display: 'grid',
              placeItems: 'center', fontSize: 22, fontWeight: 600,
            }}>
              {initials}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 className="serif" style={{ fontSize: 26, margin: 0, fontWeight: 600 }}>{pro.business_name}</h2>
                {pro.is_atlas_verified && (
                  <span style={{ background: 'rgba(245,241,232,0.25)', fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10, letterSpacing: '0.06em' }}>
                    VERIFIED
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>
                {pro.trade}{pro.secondary_trade ? ` · ${pro.secondary_trade}` : ''} · {pro.response_time}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 28, marginTop: 22, fontSize: 12, opacity: 0.92 }}>
            <div><span className="serif" style={{ fontSize: 20, fontWeight: 600 }}>{pro.rating}</span> rating ({pro.review_count} reviews)</div>
            <div><span className="serif" style={{ fontSize: 20, fontWeight: 600 }}>{pro.atlas_jobs_completed}</span> PropAtlas jobs</div>
            <div><span className="serif" style={{ fontSize: 20, fontWeight: 600 }}>{pro.rate_band || '—'}</span> rate band</div>
          </div>
        </div>

        <div className="modal-body">
          {pro.bio && <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: '0 0 16px' }}>{pro.bio}</p>}

          <div className="divider">Credentials</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>License</div>
              <div style={{ fontSize: 13, marginTop: 2 }}>{pro.license_number || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>Service areas</div>
              <div style={{ fontSize: 13, marginTop: 2 }}>{pro.service_areas?.join(', ') || '—'}</div>
            </div>
          </div>

          <div className="divider">Badges</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {pro.badges.map((b) => <Chip key={b} variant="forest">{b}</Chip>)}
          </div>
        </div>

        <div className="modal-foot">
          <button className="btn ghost" onClick={onClose}>Close</button>
          <button
            className="btn ghost"
            onClick={() => { onClose(); navigate(`/pros/${pro.id}`); }}
          >
            View full profile
          </button>
          <button className="btn primary" onClick={onRequestQuote}>Request quote</button>
        </div>
      </div>
    </div>
  );
}

function RequestQuoteModal({ pro, onClose }) {
  const { items: properties, loading: propsLoading } = useProperties();
  const [form, setForm] = useState({
    property_id: '',
    trade: pro.trade,
    description: '',
    urgency: 'week',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.property_id) { setError('Please select a property.'); return; }
    if (form.description.length < 10) { setError('Description must be at least 10 characters.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await marketplaceAPI.createBooking({
        property_id: form.property_id,
        pro_id: pro.id,
        trade: form.trade,
        description: form.description,
        urgency: form.urgency,
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 480, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-body" style={{ padding: '40px 32px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
            <h2 className="serif" style={{ fontSize: 22, color: 'var(--forest)', fontWeight: 600, margin: '0 0 8px' }}>
              Quote request sent!
            </h2>
            <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              <strong>{pro.business_name}</strong> will review your request and get back to you. You can track this in <strong>Bookings</strong>.
            </p>
          </div>
          <div className="modal-foot">
            <button className="btn primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
              Request quote
            </div>
            <h2 className="serif" style={{ fontSize: 20, margin: '2px 0 0', color: 'var(--forest)', fontWeight: 600 }}>
              {pro.business_name}
            </h2>
          </div>
          <button onClick={onClose} className="btn ghost sm">✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'grid', gap: 16 }}>
            {error && <div style={{ color: 'var(--crimson)', fontSize: 13 }}>{error}</div>}

            {/* Property selector */}
            <div className="field">
              <label>Property</label>
              {propsLoading ? (
                <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Loading properties…</div>
              ) : properties.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--crimson)' }}>No properties found. Add a property first.</div>
              ) : (
                <select
                  value={form.property_id}
                  onChange={update('property_id')}
                  style={{ height: 36, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 4, background: 'var(--card)', fontSize: 13, width: '100%' }}
                >
                  <option value="">Select a property…</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.address_line1}, {p.city}, {p.state}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Trade */}
            <div className="field">
              <label>Service type</label>
              <input value={form.trade} onChange={update('trade')} placeholder="e.g. Plumber, HVAC, Electrician" />
            </div>

            {/* Description */}
            <div className="field">
              <label>Describe the work needed</label>
              <textarea
                value={form.description}
                onChange={update('description')}
                rows={4}
                placeholder="Describe what needs to be done, any relevant details about the property or issue…"
                style={{ resize: 'vertical', padding: '8px 10px', border: '1px solid var(--hairline)', borderRadius: 4, background: 'var(--card)', fontSize: 13, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>
                {form.description.length} / 2000 characters · minimum 10
              </div>
            </div>

            {/* Urgency */}
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 8 }}>Urgency</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {URGENCY_OPTIONS.map((u) => (
                  <button
                    key={u.value}
                    type="button"
                    onClick={() => setForm({ ...form, urgency: u.value })}
                    style={{
                      padding: '10px 8px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${form.urgency === u.value ? 'var(--forest)' : 'var(--hairline)'}`,
                      background: form.urgency === u.value ? 'rgba(31,58,46,0.06)' : 'var(--card)',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: form.urgency === u.value ? 'var(--forest)' : 'var(--ink-2)' }}>
                      {u.label}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 2 }}>{u.sub}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={submitting || propsLoading || properties.length === 0}>
              {submitting ? 'Sending…' : 'Send quote request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
